import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  getQuestionarioForm,
  submitQuestionarioAnswer,
} from '../services/api.js';

const Questionario = ({ contentId, onComplete }) => {
  const [questionario, setQuestionario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Store answers locally encoded by question id: { [questionId]: answerValue }
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const data = await getQuestionarioForm(contentId);
        setQuestionario(data);
      } catch (err) {
        setError(err.message || 'Erro ao carregar o questionário.');
      } finally {
        setLoading(false);
      }
    };

    if (contentId) {
      fetchForm();
    }
  }, [contentId]);

  const handleOptionChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleTextChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!questionario?.questoes) return;
    try {
      setSubmitting(true);
      setError(null);

      // Submit each answer individually based on provided API docs
      for (const questao of questionario.questoes) {
        const answerData = {
          id_curso_conteudo_usuario: contentId,
          id_curso_questionario: questao.id_curso_questionario,
        };

        const answer = answers[questao.id_curso_questionario];
        if (!answer) continue;

        if (questao.tipo === 'dissertativa') {
          answerData.nm_resposta = String(answer);
        } else if (questao.tipo === 'objetiva') {
          answerData.id_curso_questionario_questao = parseInt(answer, 10);
        }

        await submitQuestionarioAnswer(answerData);
      }

      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err.message || 'Erro ao enviar respostas.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity='error'>{error}</Alert>;
  }

  if (
    !questionario ||
    !questionario.questoes ||
    questionario.questoes.length === 0
  ) {
    return (
      <Alert severity='info'>
        Nenhuma questão disponível para este questionário.
      </Alert>
    );
  }

  // Check if all objective questions are answered. Text fields are optional.
  const allAnswered = questionario.questoes.every(q => {
    if (q.tipo !== 'objetiva') return true;
    return (
      answers[q.id_curso_questionario] &&
      String(answers[q.id_curso_questionario]).trim() !== ''
    );
  });

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {questionario.questoes
        .sort((a, b) => a.nr_ordem - b.nr_ordem)
        .map(questao => (
          <Paper
            key={questao.id_curso_questionario}
            elevation={0}
            variant='outlined'
            sx={{ p: 3, mb: 3, borderRadius: 2 }}
          >
            <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
              {questao.nr_ordem}. {questao.enunciado}
            </Typography>

            {questao.tipo === 'objetiva' && questao.alternativas ? (
              <FormControl component='fieldset' sx={{ mt: 1, width: '100%' }}>
                <RadioGroup
                  value={answers[questao.id_curso_questionario] || ''}
                  onChange={e =>
                    handleOptionChange(
                      questao.id_curso_questionario,
                      e.target.value
                    )
                  }
                >
                  {questao.alternativas.map(alt => (
                    <FormControlLabel
                      key={alt.id_curso_questionario_questao}
                      value={alt.valor || alt.id_curso_questionario_questao}
                      control={<Radio color='primary' />}
                      label={alt.texto}
                      sx={{
                        mb: 1,
                        bgcolor: '#fafafa',
                        borderRadius: 1,
                        pr: 2,
                        '&:hover': { bgcolor: '#f0f0f0' },
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  placeholder='Sua resposta...'
                  value={answers[questao.id_curso_questionario] || ''}
                  onChange={e =>
                    handleTextChange(
                      questao.id_curso_questionario,
                      e.target.value
                    )
                  }
                />
              </Box>
            )}
          </Paper>
        ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant='contained'
          color='primary'
          size='large'
          disabled={submitting || !allAnswered}
          onClick={handleSubmit}
          sx={{ minWidth: 200 }}
        >
          {submitting ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Enviar Respostas'
          )}
        </Button>
      </Box>
    </Box>
  );
};

import PropTypes from 'prop-types';
Questionario.propTypes = {
  contentId: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
};

export default Questionario;
